import React, { useCallback, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import JobHistory from '../Job/JobHistory';
import { Flex } from 'rebass';
import useNotify from '../../hooks/useNotify';
import { Job } from '../BackflowTest/types';
import RecycledWaterInspectionDetail from './RecycledWaterInspectionDetail';
import Button from '../Button/Button';
import { PortalWithState } from 'react-portal';
import RecycledWaterInspectionSchedulingModal from './RecycledWaterInspectionSchedulingModal';
import RecycledWaterInspectionEditModal from './RecycledWaterInspectionEditModal';
import { EmailInput, PhoneInput, ProfileType } from '../Profile/types';
import RecycledWaterInspectionResultsModal from './RecycledWaterInspectionResultsModal';

export const GET_JOB = gql`
  query RecycleWaterJob($where: JSON) {
    jobs(where: $where) {
      id
      recycledWaterInspection {
        jobId
        waterSite {
          id
          name
        }
        supervisor {
          id
          type
          name
          firstName
          lastName
          phones {
            id
            subType
            value
            extension
          }
          emails {
            id
            subType
            value
          }
        }
        contact {
          id
          type
          name
          firstName
          lastName
          phones {
            id
            subType
            value
            extension
          }
          emails {
            id
            subType
            value
          }
        }
      }
      address {
        id
        street1
        street2
        city
        state
        zip
        zipExt
      }
      formData
      attachments {
        id
        contentType
        url
        thumb(size: 500)
        profile {
          id
          name
        }
        created
      }
      comments {
        id
        type
        comment
        profile {
          id
          name
        }
        created
      }
      statusChanges {
        id
        fromStatus
        toStatus
        profile {
          id
          name
        }
        created
      }
    }
  }
`;

type RecycledWaterInspectionThirdPaneProps = {
  jobId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
  reload?: () => void;
};

// const downloadPdf = (jobId?: string) => {
//   if (!jobId) {
//     return
//   }
//   const url = `${config.apiBase}/test-reports/${jobId}?token=${getToken()}`
//   window.open(url)
// }

type RecycledWaterInspection = {
  waterSiteId: string;
  waterSite: {
    id: string;
    name: string;
  };
  contact: {
    id: string;
    name: string;
    type: ProfileType;
    firstName: string;
    lastName: string;
    emails: EmailInput[];
    phones: PhoneInput[];
  };
  supervisor: {
    id: string;
    name: string;
    type: ProfileType;
    firstName: string;
    lastName: string;
    emails: EmailInput[];
    phones: PhoneInput[];
  };
};

export type JobWithRwInspection = Job & {
  recycledWaterInspection: RecycledWaterInspection;
};

export default function RecycledWaterInspectionThirdPane({
  jobId,
  reload,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: RecycledWaterInspectionThirdPaneProps): JSX.Element {
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    jobs: JobWithRwInspection[];
  }>(GET_JOB, {
    skip: !jobId,
    variables: { where: { id: jobId } },
  });

  const advanceAndReload = useCallback(() => {
    if (onNext && nextEnabled) {
      onNext();
    }
    if (reload) {
      reload();
    }
  }, [onNext, nextEnabled]);

  const { showError } = useNotify();

  const [job, setJob] = useState<JobWithRwInspection>();

  useEffect(() => {
    if (itemData && itemData?.jobs?.length) {
      setJob(itemData?.jobs[0]);
    }
  }, [jobId, itemData, setJob]);

  const downloadReport = useCallback(() => {
    if (job) {
      // downloadPdf(jobId)
    }
  }, [job?.id]);

  if (itemError) {
    throw itemError;
  }

  return (
    <ThirdPane visible={!!jobId}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={job ? job?.id : 'Loading...'}
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      >
        {job && (
          <>
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button mr={10} variant="outline" onClick={openPortal}>
                    Send Notice
                  </Button>
                  {portal(
                    <RecycledWaterInspectionSchedulingModal
                      job={job}
                      onClose={closePortal}
                      mailOptions={{
                        subject: '',
                        body: ``,
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    mr={10}
                    icon="edit"
                    variant="outline"
                    onClick={openPortal}
                  >
                    Edit
                  </Button>
                  {portal(
                    <RecycledWaterInspectionEditModal
                      action="edit"
                      jobId={job.id}
                      rwInspection={{
                        ...job.recycledWaterInspection,
                        job: {
                          addressId: job.address.id,
                          scheduled: job.scheduled,
                        },
                      }}
                      onClose={closePortal}
                    />
                  )}
                </>
              )}
            </PortalWithState>
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button mr={10} variant="outline" onClick={openPortal}>
                    Enter Results
                  </Button>
                  {portal(
                    <RecycledWaterInspectionResultsModal
                      jobId={job.id}
                      onClose={closePortal}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </>
        )}
      </ThirdPaneHeader>
      <ThirdPaneContent>
        {job && (
          <>
            <Flex height="100%" flexDirection="row" flexGrow={1} flexShrink={1}>
              <Flex
                flexDirection="column"
                flexGrow={1}
                flexShrink={1}
                height="100%"
                width="50%"
                overflowY="scroll"
              >
                {job?.recycledWaterInspection && (
                  <RecycledWaterInspectionDetail job={job} />
                )}
              </Flex>
              <Flex
                flexDirection="column"
                flexGrow={1}
                flexShrink={1}
                height="100%"
                width="50%"
                sx={{ borderLeft: '1px solid', borderLeftColor: 'tableBorder' }}
              >
                <JobHistory
                  jobId={job.id}
                  comments={job.comments}
                  statusChanges={job.statusChanges}
                  attachments={job.attachments}
                  refetch={refetch}
                />
              </Flex>
            </Flex>
          </>
        )}
      </ThirdPaneContent>
      {/*{false && (*/}
      {/*  <ThirdPaneFooter>*/}
      {/*    <Box flexGrow={1} textAlign="right">*/}
      {/*      <Button onClick={downloadReport} icon="file-download" variant="secondary">*/}
      {/*        PDF*/}
      {/*      </Button>*/}
      {/*    </Box>*/}
      {/*  </ThirdPaneFooter>*/}
      {/*)}*/}
    </ThirdPane>
  );
}
